import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApprovalCancelled, useApproval } from "@/app/approval";
import { usePos } from "@/app/context";
import { MoneyText } from "@/components/MoneyText";
import type { SyncResultRecord } from "@/lib/db";

/** The approver's display name inside the one-use approval token (identity-service `name`). */
export function approverName(token: string): string | null {
  const part = token.split(".")[1];
  if (!part) {
    return null;
  }
  try {
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/"))) as {
      name?: unknown;
      sub?: unknown;
    };
    return typeof json.name === "string"
      ? json.name
      : typeof json.sub === "string"
        ? json.sub
        : null;
  } catch {
    return null;
  }
}

const REASONS = new Set([
  "sold_elsewhere",
  "snapshot_stale",
  "staff_unverified",
  "external_listing",
  "rank_missing",
  "queue_over_limit",
  "total_mismatch",
  "tendered_below_total",
  "other_shift",
]);

/**
 * One 要確認 of the offline upload (T07-33): a card sold elsewhere while offline (`conflict` — the
 * cash is kept, the stock was not touched) or a sale the server refused (`rejected`). Red, with
 * what was sold, the cash taken, when, and what to do; it stays until a manager confirms with their
 * PIN that it was handled in the back office (refund / other card — `POST
 * /admin/pos/sales/{id}/review`).
 */
export function ConflictCard({ record }: { record: SyncResultRecord }) {
  const { t, i18n } = useTranslation();
  const { db } = usePos();
  const { ask } = useApproval();
  const [busy, setBusy] = useState(false);
  const time = record.clientTime
    ? new Intl.DateTimeFormat(i18n.resolvedLanguage ?? "ja", {
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Tokyo",
      }).format(new Date(record.clientTime))
    : "—";
  const reasons = (record.reasons ?? []).filter((r) => REASONS.has(r));

  const resolve = async () => {
    setBusy(true);
    try {
      const token = await ask({
        permission: "pos.refund",
        action: "pos.offline_review",
        subject: record.saleId ?? record.idempotencyKey,
        amount: record.total ?? null,
        reason: t("sync.resolve_reason"),
      });
      await db.syncResults.update(record.idempotencyKey, {
        resolvedAt: Date.now(),
        resolvedBy: approverName(token),
      });
    } catch (error) {
      if (!(error instanceof ApprovalCancelled)) {
        throw error;
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      aria-label={t("sync.review_card", { no: record.tempReceiptNo ?? "" })}
      className="flex flex-col gap-2 rounded-lg border-2 border-status-problem bg-status-problem/10 p-4"
    >
      <header className="flex items-center gap-2 text-lg font-semibold text-pos-problem">
        <TriangleAlert aria-hidden className="size-6" />
        {record.status === "conflict" ? t("sync.conflict_title") : t("sync.rejected_title")}
      </header>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        <dt className="text-muted-foreground">{t("sync.temp_no")}</dt>
        <dd className="font-semibold">{record.tempReceiptNo ?? "—"}</dd>
        <dt className="text-muted-foreground">{t("sync.time")}</dt>
        <dd>{time}</dd>
        <dt className="text-muted-foreground">{t("sync.items")}</dt>
        <dd>
          <ul>
            {(record.summary?.items ?? []).map((item, index) => (
              <li key={`${item.title}-${String(index)}`}>
                {item.title}
                {item.qty > 1 ? ` ×${String(item.qty)}` : ""}
              </li>
            ))}
          </ul>
        </dd>
        <dt className="text-muted-foreground">{t("sync.cash_taken")}</dt>
        <dd>
          <MoneyText amount={record.summary?.total ?? record.total ?? 0} />
        </dd>
      </dl>
      {reasons.length ? (
        <ul className="list-disc pl-5">
          {reasons.map((reason) => (
            <li key={reason}>{t(`sync.reason_${reason}`)}</li>
          ))}
        </ul>
      ) : record.errorMessage || record.errorCode ? (
        <p>{record.errorMessage ?? record.errorCode}</p>
      ) : null}
      <p className="font-medium">{t("sync.advice")}</p>
      {record.resolvedAt ? (
        <p className="text-pos-ok">{t("sync.resolved", { name: record.resolvedBy ?? "" })}</p>
      ) : (
        <button
          type="button"
          onClick={() => void resolve()}
          disabled={busy}
          className="min-h-pos-touch self-start rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground disabled:opacity-50"
        >
          {t("sync.resolve")}
        </button>
      )}
    </article>
  );
}
